import React from 'react';
import { graphql } from 'gatsby';
import isNil from 'utils/isNil';
import TwoSidesSection from 'components/TwoSidesSection';
import DynamicHero from 'components/Hero/DynamicHero';
import ComparisonCardSection from 'components/ComparisonCardSection';
import { SectionDescriptionWithImage } from 'components/Sections/SectionDescriptionWithImage';
import { CircleCheckboxIcon } from 'components/Brand/Icons/CircleCheckboxIcon';
import useGlobalHeader from 'hooks/useGlobalHeader';
import useGlobalFooter from 'hooks/useGlobalFooter';
import SEO, { createSEO } from '../components/SEO';
import { WebsiteImage, DescriptionWithIcon } from '../components/TitleDescription';
import WebsiteLayout from '../layouts/websiteLayout';

const ComparisonPage = props => {
  const heroData = props.data.contentfulDynamicHero;
  const pageImages = props.data.allContentfulWebsiteImage;
  const header = useGlobalHeader();
  const footer = useGlobalFooter();
  let logoFieldEdge;
  let logoJobber;
  let logoKicksev;
  let logoSmart;
  let logoHcp;
  let img1;
  let img2;
  let img3;
  let img4;
  let logoSuccessware;
  let logoHcpJobber;

  pageImages.edges.forEach(imageItem => {
    if (imageItem.node.internalName === 'Field Edge Logo') {
      logoFieldEdge = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'jobber') {
      logoJobber = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'Kicksev') {
      logoKicksev = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'Smart Service') {
      logoSmart = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'hcp-logo') {
      logoHcp = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'Mobile - Image 68') {
      img1 = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'SMS texting and GPS Tracking - Image 69') {
      img2 = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'Reporting Dashboard') {
      img3 = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'SMS Texting and GPS Tracking  - Image 71') {
      img4 = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'successware') {
      logoSuccessware = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'housecall-vs-jobbers') {
      logoHcpJobber = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    }
  });

  const seo = createSEO(props.data.contentfulSeo, props);

  const sideImage1 = [
    {
      title: 'Customer Service',
      file: { url: img2 },
    },
  ];

  const sideImage2 = [
    {
      title: 'Data and Insights',
      file: { url: img3 },
    },
  ];

  const sideImage3 = [
    {
      title: 'Boost Efficiency',
      file: { url: img4 },
    },
  ];

  const cardInfo = [
    {
      title: 'ServiceTitan vs FieldEdge',
      link: '/comparison/servicetitan-vs-fieldedge',
      logo: logoFieldEdge,
    },
    {
      title: 'ServiceTitan vs Jobber',
      link: '/comparison/servicetitan-vs-jobber',
      logo: logoJobber,
    },
    {
      title: 'ServiceTitan vs Kickserv',
      link: '/comparison/servicetitan-vs-kickserv',
      logo: logoKicksev,
    },
    {
      title: 'ServiceTitan vs Smart Service',
      link: '/comparison/servicetitan-vs-smart-service',
      logo: logoSmart,
    },
    {
      title: 'ServiceTitan vs Housecall Pro',
      link: '/comparison/servicetitan-vs-housecall-pro',
      logo: logoHcp,
    },
    {
      title: 'ServiceTitan vs Successware',
      link: '/comparison/servicetitan-vs-successware',
      logo: logoSuccessware,
    },
    {
      title: 'ServiceTitan vs Housecall Pro & Jobber',
      link: '/comparison/housecall-pro-vs-jobber',
      logo: logoHcpJobber,
    },
  ];

  return (
    <WebsiteLayout header={header} footer={footer} hero={heroData}>
      <SEO {...seo} />
      {heroData && <DynamicHero data={heroData} images={pageImages.edges} />}

      <SectionDescriptionWithImage
        titlePart={
          <div style={{ marginBottom: '24px' }}>
            <h2>Improve Sales</h2>
          </div>
        }
        descriptionOnePart={
          <DescriptionWithIcon iconImage={<CircleCheckboxIcon color="#009DE6" />}>
            Improve conversion rates and job averages with a powerful visual sales experience.
          </DescriptionWithIcon>
        }
        descriptionTwoPart={
          <DescriptionWithIcon iconImage={<CircleCheckboxIcon color="#009DE6" />}>
            Book more opportunities by tracking how your office staff convert calls into
            appointments.
          </DescriptionWithIcon>
        }
        descriptionThreePart={
          <DescriptionWithIcon iconImage={<CircleCheckboxIcon color="#009DE6" />}>
            Generate more leads by measuring the success of every marketing campaign.
          </DescriptionWithIcon>
        }
        descriptionAdditionalPart={null}
        imagePart={<WebsiteImage src={img1} alt="Pricebook" style={{ maxWidth: '100%' }} />}
        reversed={false}
        backgroundColor="white"
      />

      <TwoSidesSection
        reversed
        backgroundColor="Alice Blue"
        backgroundImageTypeWithPosition=""
        title="Customer Service"
        description={`In the office, ServiceTitan's Phone Integration will automatically pull up your customer's profile when they call.

When dispatching a tech, your customers will feel at ease about a stranger entering their home when they get a photo and bio of your tech.

Create a remarkable experience and reduce the service window by allowing your customers to see how far away the tech is on a map in real-time.

Your customers won't stop talking about you.`}
        image={sideImage1}
        video={null}
        videoType=""
        imageSideCornerType=""
        videoSideBackColor="transparent"
      />

      <TwoSidesSection
        reversed={false}
        backgroundColor="white"
        backgroundImageTypeWithPosition=""
        title="Data and Insights"
        description={`Because ServiceTitan powers your office, your tech's mobile app, and integrates with your phones, all your data is in one place.

The Dashboard shows key metrics at your finger tips, such as today's revenue, tech and CSR performance, and more.

Meet your company goals and discover growth opportunities with better data.

You can stop with the spreadsheets. Finally.`}
        image={sideImage2}
        video={null}
        videoType=""
        imageSideCornerType=""
        videoSideBackColor="transparent"
      />

      <TwoSidesSection
        reversed
        backgroundColor="Alice Blue"
        backgroundImageTypeWithPosition=""
        title="Boost Efficiency"
        description={`See all customer information when they call and track all activity (call recordings, emails, text messages) related to every opportunity.

Limit time-consuming field-to-office interaction with powerful purchase-order management tools and time-stamping on mobile.

Automate routine office business tasks—like appointment reminders—to save at least 5 hours per week.`}
        image={sideImage3}
        video={null}
        videoType=""
        imageSideCornerType=""
        videoSideBackColor="transparent"
      />

      <ComparisonCardSection
        backgroundColor="#FFF"
        title="See How We Compare To The Competition"
        description="Find the right fit for your business"
        cardInfo={cardInfo}
      />
    </WebsiteLayout>
  );
};

export default ComparisonPage;

export const ComparisonPageQuery = graphql`
  query ComparisonPageQuery {
    contentfulSeo(internalName: { eq: "Comparison" }) {
      ...Seo
    }
    contentfulDynamicHero(contentful_id: { eq: "6cgYfIulOxX7q599nVJohd" }) {
      ...DynamicHero
    }
    allContentfulWebsiteImage(
      filter: {
        contentful_id: {
          in: [
            "4cmbjg6gLkwPqlEyjh35ts" #bg-main-Sky-Blue
            "1495f0LuHprVidWlYKVkuU" #bg-main-White
            "RRDSN31hIl6gLin4qTAfL" #bg-main-Prussian
            "32gRHoDZlO4nVjVCQ9QweN" #bg-main-Azure
            "CsmFL9cUG5lF6g9wJK9vJ" #Software Advice Black
            "1BCz8kg8isBnEazo3RyezT" #Google Black
            "2OvSHiJkjSVL1VB48kbmli" #Capterra Black
            "7n9xWOg3bq62jNFOGlwOUz" #Google White (2)
            "4Hv9OEOfQi7cgyPqVdGkzE" #Capterra White (2)
            "7Dg7v6YoqGBSDhLf8wEdaL" #Software Advice White (2)
            "4cFD9L8In7BegDVOF5RCkJ" #Field Edge Logo
            "MIHnUcBrTtNPQrzd5uK09" #jobber
            "3JcScpHuKDAyiONWnNQinZ" #Kicksev
            "14LtrYd1YggyYapI3Gn3Nh" #Servman
            "ZsU3RmpgvzjrevAlwqOkq" #Smart Service
            "5eDcMiIuiWcRuwlBX2itHq" #Successware 21
            "3KSQIFuxTh4xPbB4Yxv2bE" #hcp-logo
            "4DjdeKNh5oSLqz61oXkB9w" #Mobile - Image 68
            "3LtiEQBrde6Sh8P6qM3INz" #SMS texting and GPS Tracking - Image 69
            "2YErRjdqIhlBbO6TZRsL07" #Reporting Dashboard
            "23r5mQLHpgJs7ladkIhRS8" #SMS Texting and GPS Tracking  - Image 71
            "26RipHaMNfPnfkXnQXXv9b" #housecall-vs-jobbers
            "6LtBXI7BWZQ3Kvy7SwupkN" #successware
          ]
        }
      }
    ) {
      edges {
        node {
          contentful_id
          internalName
          image {
            file {
              url
            }
          }
        }
      }
    }
  }
`;
